import { useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import TableHeader from '@/components/TableHeader'
import HistoryIcon from '@mui/icons-material/History'
import { useGetLoggingQuery } from '@/pages/main/components/logging-gates/api-slice'
import TableEmptyPlaceholder from '@/components/TableEmptyPlaceholder'
import { useDispatch, useSelector } from 'react-redux'
import { 
  clearOpenHistoryParams, 
  selectOpenHistoryParams, 
  selectShowOpenHistory 
} from './modalsSlice';
import SearchInput from '@/components/SearchInput'

export const loggingTableHead = [
  {
    id: '',
    label: '',
    alignRight: false,
  },
  {
    id: 'date',
    label: 'Дата',
    alignRight: false
  },
  {
    id: 'id',
    label: 'ID',
    alignRight: false
  },
  {
    id: 'open',
    label: 'Статус',
    alignRight: false
  },
  {
    id: 'user',
    label: 'Пользователь',
    alignRight: true
  }
]

const OpenHistoryModal = () => {
  const show = useSelector(selectShowOpenHistory);
  const modalParams = useSelector(selectOpenHistoryParams);
  const dispatch = useDispatch();
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [searchInputKey, setSearchInputKey] = useState(0)
  const [order, setOrder] = useState('asc')
  const [selected, setSelected] = useState([])
  const ref = useRef(null)
  const [apiPage, setApiPage] = useState(1)
  const [orderBy, setOrderBy] = useState('number')
  const emptyPlaceholder = `${t('list_empty')}\n${t('open-history-list_explained')}`
  const { 
    data, 
    isSuccess, 
    isError, 
    isLoading, 
    isFetching 
  } = useGetLoggingQuery({
      channelId: modalParams?.channelId,
      size: 50,
      page: apiPage,
    }, {
      skip: !show || !modalParams?.channelId
    })

  const list = search.trim()
    ? data?.filter((item) => {
        const term = search.trim().toLowerCase()
        const username = String(item?.username || '').toLowerCase()
        const id = String(item?.id ?? '')
        return username.includes(term) || id.includes(term)
      })
    : data;

  const handleSearch = (value) => {
    setSearch(value)
  }

  useEffect(() => {
    if (!show) {
      setSearch('')
      setApiPage(1)
      setSelected([])
      setSearchInputKey((key) => key + 1)
    }
  }, [show])
  

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((item) => item.id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }

  return (
    <Dialog 
      open={show} 
      onClose={() => dispatch(clearOpenHistoryParams())}
    >
      <DialogTitle id="scroll-dialog-title">{t('trigger_history')}</DialogTitle>
      <DialogContent>
        {/* placeholder={t('enter_title')} */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            mb: 2
          }}>
          <SearchInput
            key={searchInputKey}
            placeholder={t('enter_title')}
            onChange={handleSearch}
            disabled={isLoading || isError}
          />
        </Stack>
        <TableContainer>
          <Table stickyHeader ref={ref}>
            <TableHeader
              order={order}
              orderBy={orderBy}
              headLabel={loggingTableHead}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {(isLoading || (isFetching && !data?.length)) && (
                <TableRow>
                  <TableCell colSpan={loggingTableHead.length} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {isSuccess &&
                list?.map((table) => {
                  const isItemSelected = selected.indexOf(table.id) !== -1
                  return (
                    <TableRow
                      key={table?.id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, table?.id)}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {
                          table?.created_at
                            ? format(
                                new Date(String(table.created_at).replace('Z', '')),
                                'dd.MM.yy / HH:mm:ss'
                              )
                            : '—'
                        }
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        <Typography variant="subtitle2" noWrap>
                          {table?.id}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        {table?.open ? t('open') : t('close')}
                      </TableCell>
                      <TableCell align="left">{table?.username}</TableCell>
                    </TableRow>
                  )
                })
              }
            </TableBody>
          </Table>
        </TableContainer>
        {
          isSuccess && !!data?.length &&! list?.length && (
            <Alert severity="info" sx={{ mt: 3 }}>
              {t('no_results_found')}
            </Alert>
          )
        }
        {
          isSuccess && !data?.length && (
            <TableEmptyPlaceholder
              icon={HistoryIcon}
              message={emptyPlaceholder}
            />
          )
        }
        {isError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>{t('alert')}</AlertTitle>
            {t('error_occurred')}
          </Alert>
        )}
        <Stack
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "row"
          }}>
          <Button
            variant="text"
            color="error"
            disabled={apiPage === 1}
            onClick={() => setApiPage((prev) => prev - 1)}
          >
            {t('back')}
          </Button>
          <Button
            variant="text"
            color="success"
            disabled={!data?.length}
            onClick={() => {
              setApiPage((prev) => prev + 1)
              ref.current?.scrollIntoView()
            }}
          >
            {t('next')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default OpenHistoryModal;
